// Datatable.js
import React, { Fragment, useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import axios from "axios";

const Datatable = ({ myData, myClass, pagination }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [nextStatusType, setNextStatusType] = useState("");
  const [currentOrderItem, setCurrentOrderItem] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [disputeReason, setDisputeReason] = useState("");
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [isProofModalOpen, setProofModalOpen] = useState(false);
  const [proofType, setProofType] = useState("shipped");
  const [proofDataRow, setProofDataRow] = useState(null);

  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setImages((prev) => [...prev, ...selectedFiles]);
    setImagePreviews((prev) => [
      ...prev,
      ...selectedFiles.map((file) => URL.createObjectURL(file)),
    ]);
    e.target.value = ""; // reset input
  };


  useEffect(() => {
    setData(myData);
  }, [myData]);

  const handleDelete = (index) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) {
      const newData = data.filter((_, idx) => idx !== index);
      setData(newData);
      toast.success("Élément supprimé avec succès !");
    }
  };

  const onOpenModal = (orderItem, type) => {
    setNextStatusType(type)
    setCurrentOrderItem(orderItem);
    setOpen(true);
  };
  const onCloseModal = async () => {
    setOpen(false);
    setTrackingNumber("");
    setDisputeReason("");
    setImages([]);
    setImagePreviews([]);
  };
  const onCloseProofModal = async () => {
    setProofModalOpen(false);
    setProofDataRow(null);
  };
  const onOpenProofModal = (orderItem, type) => {
    setProofModalOpen(true);
    setProofType(type);
    setProofDataRow(orderItem);
  };

  const handleValidate = async (orderItem, type) => {
    setLoading(true)
    setCurrentOrderItem(orderItem);
    setNextStatusType(type);
    await updateOrderItemStatus(orderItem, type); // ✅ pass as arguments
  };


  const handleSubmit = async () => {
    if (!currentOrderItem || modalLoading) return; // block if already submitting
    setModalLoading(true);
    await updateOrderItemStatus(currentOrderItem, nextStatusType);
    setModalLoading(false);
    onCloseModal();
  };


  const removeImage = (indexToRemove) => {
    const updatedImages = images.filter((_, idx) => idx !== indexToRemove);
    const updatedPreviews = imagePreviews.filter((_, idx) => idx !== indexToRemove);

    setImages(updatedImages);
    setImagePreviews(updatedPreviews);
  };


  const updateOrderItemStatus = async (orderItem, statusType) => {
    if (!orderItem) {
      toast.error("Issue with your order!");
      return;
    }

    let payload;

    if (statusType === "Shipped") {
      payload = new FormData();
      payload.append("status", statusType);
      payload.append("tracking_number", trackingNumber);

      images.forEach((img) => {
        payload.append("images[]", img);
      });
    } else {
      payload = {
        status: statusType,
        dispute_reason: statusType === "Dispute" ? disputeReason : undefined,
      };
    }

    try {
      const response = await axios.patch(
        `${baseUrl}/update-order-item/${orderItem.id}/`,
        payload,
        {
          headers: {
            "Content-Type":
              statusType === "Shipped" ? "multipart/form-data" : "application/json",
          },
        }
      );

      const updatedData = data.map((item) =>
        // item.id === orderItem.id ? { ...item, row: response.data.item } : item
        item.id === orderItem.id ? response.data.item : item
      );
      setData(updatedData);
      toast.success("Statut mis à jour avec succès !");
      setImages([]);
      setImagePreviews([]);

    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut !");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const getBadgeClass = (status) => {

    switch (status) {
      case "Paid":
        return "badge badge-success";
      case "Awaiting Authentication":
      case "PaymentPending":
        return "badge badge-warning";
      case "Payment Failed":
      case "Cancelled":
      case "Dispute":
        return "badge badge-danger";
      case "Delivery":
      case "Delivered":
        return "badge badge-primary";
      default:
        return "badge badge-secondary";
    }
  };

  const translateStatus = (status) => {
    switch (status) {
      case "PaymentPending":
        return "En attente de paiement";
      case "PaymentDeclined":
        return "Paiement refusé";
      case "Pending":
        return "En attente";
      case "Accepted":
        return "Accepté";
      case "Processing":
        return "En préparation";
      case "Shipped":
        return "Expédié";
      case "Received":
        return "Reçu";
      case "Returned":
        return "Retourné";
      case "Completed":
        return "Terminé";
      case "Cancelled":
        return "Annulé";
      case "Refunded":
        return "Remboursé";
      case "Dispute":
        return "Litige";
      default:
        return status;
    }
  };

  const translateDisputeResolutionStatus = (status) => {
    console.log("Dispute Resolution Status:", status);  // Debug log

    switch (status) {
      case "Unresolved":
        return "Non résolu";
      case "Resolved":
        return "Résolu";
      case "Refunded":
        return "Remboursé au client";
      case "Rejected":
        return "Litige rejeté";
      default:
        return status;
    }
  };

  const columns = [
    {
      name: "Nom du produit",
      selector: (row) => row.product_details?.title || "N/A",
      sortable: true,
      reorder: true,
    },
    {
      name: "Commande",
      selector: (row) => row.order,
      sortable: true,
    },
    {
      name: "Prix",
      selector: (row) => `${row.product_details?.price} €` || "N/A",
      sortable: true,
    },
   {
  name: "Date de réservation",
  selector: (row) =>
    row.booked_at
      ? new Date(row.booked_at).toLocaleDateString("fr-FR")
      : "-",
  sortable: true,
  right: true,
},
    {
      name: "Date",
      selector: (row) =>
        new Date(row.order_details?.created_at).toLocaleDateString("fr-FR"),
      sortable: true,
      right: true,
    },

    {
      name: "Couleur",
      selector: (row) => row.product_details?.color?.title || "N/A",
      sortable: true,
    },
    {
      name: "Date de retour",
      selector: (row) =>
        row.return_at
          ? new Date(row.return_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          : "—",
      sortable: true,
      right: true,
    },
    {
      name: "Status",
      selector: (row) => {
        console.log("Row data:", row); // ✅ Logs the full row object

        return (
          <span className={getBadgeClass(row.status)}>
            {translateStatus(row.status)}
            {row.status === "Dispute" && row.is_disputed && row.dispute_resolution_status
              ? ` (${translateDisputeResolutionStatus(row.dispute_resolution_status)})`
              : ""}
          </span>
        );
      },
      sortable: true,
      format: (row) => translateStatus(row?.status) || "N/A",
    },
    {
      name: "Proofs",
      selector: (row) => {
        return (
          <span style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "8px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "6px"
          }}>
            {["Shipped", "Received", "Returned", "Completed", "Cancelled", "Refunded", "Dispute"].includes(row.status) &&
              <button
                onClick={() => onOpenProofModal(row, "shipped")}
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#2563eb",
                  color: "#fff",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  transition: "background-color 0.3s",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "160px",
                  fontSize: "13px",
                }}
              >
                📤 View Sent Proof
              </button>
            }
            {["Received"].includes(row.status) &&
              <button
                onClick={() => onOpenProofModal(row, "return")}
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#16a34a",
                  color: "#fff",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  transition: "background-color 0.3s",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "160px",
                  fontSize: "13px",
                }}
              >
                📥 View Received Proof
              </button>
            }

          </span>
        );
      },
      sortable: false,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div>
          {row.status === "Pending" && (
            <Button
              color="primary"
              onClick={() => handleValidate(row, "Processing")}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  En cours...
                </>
              ) : (
                "Valider"
              )}
            </Button>


          )}
          {row.status === "Processing" && (
            <Button
              color="success"
              onClick={() => onOpenModal(row, "Shipped")}
            >
              Envoyer
            </Button>
          )}
          {row.status === "Returned" && (
            <>

              <Button
                color="primary"
                onClick={() => handleValidate(row, "Completed")}
                disabled={loading}
              >
                Completed
              </Button>
              <Button
                color="success"
                onClick={() => onOpenModal(row, "Dispute")}
              >
                Rise Dispute
              </Button>
            </>
          )}
          {row.status === "Dispute" && row.is_disputed && row.dispute_resolution_status !== "Unresolved" && (
            <Button
              color="primary"
              onClick={() => handleValidate(row, "Completed")}
              disabled={loading}
            >
              Resolved
            </Button>

          )}

        </div>
      ),
    },
  ];

  return (
    <div>
      <Fragment>
        <DataTable
          title="Gérer les commandes"
          data={data}
          columns={columns}
          className={myClass}
          pagination={pagination}
          highlightOnHover
        />


        <Modal isOpen={open} toggle={onCloseModal}>
          <ModalHeader toggle={onCloseModal}>Entrer le numéro de suivi</ModalHeader>
          <ModalBody>
            <Form>
              {nextStatusType === "Shipped" && (
                <>
                  <FormGroup>
                    <Label htmlFor="tracking-number">Numéro de suivi :</Label>
                    <Input
                      type="text"
                      id="tracking-number"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="images">Upload Images:</Label>
                    <Input
                      type="file"
                      id="images"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <div
                      className="d-flex flex-nowrap mt-2"
                      style={{
                        overflowX: "auto",
                        gap: "10px",
                        paddingBottom: "4px",
                      }}
                    >
                      {imagePreviews.map((preview, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            width: "100px",
                            height: "100px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            flexShrink: 0,
                            border: "1px solid #ddd",
                          }}
                        >
                          <img
                            src={preview}
                            alt={`Preview ${index}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            style={{
                              position: "absolute",
                              top: "2px",
                              right: "2px",
                              backgroundColor: "#ff4d4f",
                              color: "#fff",
                              border: "none",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              fontSize: "12px",
                              cursor: "pointer",
                              lineHeight: "18px",
                              textAlign: "center",
                              padding: 0,
                            }}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                </>
              )}

              {nextStatusType === "Dispute" && (
                <FormGroup>
                  <Label htmlFor="dispute-reason">Dispute Reason:</Label>
                  <Input
                    type="textarea"
                    id="dispute-reason"
                    rows="4"
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder="Enter reason for dispute..."
                  />
                </FormGroup>
              )}
            </Form>
          </ModalBody>

          <ModalFooter>
            <Button color="primary" onClick={handleSubmit} disabled={modalLoading}>
              {modalLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  En cours...
                </>
              ) : (
                "Valider"
              )}
            </Button>

            <Button color="secondary" onClick={onCloseModal} disabled={modalLoading}>
              Fermer
            </Button>

          </ModalFooter>
        </Modal>

        <Modal isOpen={isProofModalOpen} toggle={onCloseProofModal}>
          <ModalHeader toggle={onCloseProofModal}>{proofType.toUpperCase()} PROOF</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label htmlFor="tracking-number">TRACKING NUMBER</Label>
              <Input
                type="text"
                id="tracking-number"
                value={proofType === "shipped" ? (proofDataRow?.tracking_number) : (proofDataRow?.return_tracking_number) || ""}
                disabled={true}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="images">{proofType.toUpperCase()} IMAGES</Label>

              <div
                className="d-flex flex-nowrap mt-2"
                style={{
                  overflowX: "auto",
                  gap: "10px",
                  paddingBottom: "4px",
                }}
              >
                {proofDataRow?.order_item_images?.
                  filter((img) => img.image_type === proofType)
                  .map((imageRecord, index) => (//show where image.image_type = proofType
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        width: "100px",
                        height: "100px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        flexShrink: 0,
                        border: "1px solid #ddd",
                      }}
                    >
                      <a href={imageRecord.image_url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={imageRecord.image_url}
                          alt={`imageRecord ${index}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "pointer" // optional: shows pointer on hover
                          }}
                        />
                      </a>


                    </div>
                  ))}
              </div>
            </FormGroup>


          </ModalBody>

        </Modal>

        <ToastContainer />
      </Fragment>
    </div>
  );

};

export default Datatable;
